import React, { useCallback, useEffect, useState } from 'react';
import { useThemeContext } from '../../hook/useThemeManager';
import { ColorScheme, theme } from '../../constants/theme';
import { FormControl, Box, Typography, Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { alpha } from '@mui/material/styles';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { EsFormWrapper } from './EsFormWrapper';
import { CustomTextField } from '../TextInput/CustomTextField';
import { strings } from '../../constants/strings';
import { validate } from '../../utils/validators';
import { isEmpty } from '../../utils/lodash';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { CustomPhoneTextField } from '../TextInput/CustomPhoneTextField';
import { StyleSheet } from 'react-native';
import { AppleIcon } from 'lucide-react';
import { GoogleIcon } from '../CustomSVGIcons/GoogleIcon';
import { Facebook } from '../CustomSVGIcons/Facebook';
import { CountryData } from 'react-phone-input-2';
import { useLocation } from 'react-router-dom';
import { captureReferralCodeFromUrl } from '../../utils/referralCapture';
import { DevAutofillButton } from '../BuzzBridge/DevAutofillButton';
import { getDevEsSignupFields, isDevAutofillEnabled } from '../../utils/devAutofill';

type Field = 'email' | 'password' | 'phoneNumber' | 'fullName';
type UserPassword = {
    email: string;
    password: string;
    fullName: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
};

type UserFormPassword = UserPassword & {
    updateFields: (fields: Partial<UserPassword>) => void;
    onCountryChange: (e: CountryData) => void;
    workspaceChoice: 'brand' | 'creator';
    onWorkspaceChoiceChange: (v: 'brand' | 'creator') => void;
};

const EXTERNAL_LOGIN = false;

// Helper function to split fullName into firstName and lastName
export const splitFullName = (fullName: string): { firstName: string; lastName: string } => {
    const names = fullName
        .trim()
        .split(/\s+/)
        .filter(name => name.length > 0);
    if (names.length === 0) {
        return { firstName: '', lastName: '' };
    }
    if (names.length === 1) {
        return { firstName: names[0], lastName: '' };
    }
    // First name is the first part, last name is everything else joined
    const firstName = names[0];
    const lastName = names.slice(1).join(' ');
    return { firstName, lastName };
};

// Helper function to validate fullName has at least two names
export const validateFullName = (fullName: string): boolean => {
    const names = fullName
        .trim()
        .split(/\s+/)
        .filter(name => name.length > 0);
    return names.length >= 2;
};

const Signup = ({
    email,
    password,
    updateFields,
    phoneNumber,
    onCountryChange,
    fullName,
    workspaceChoice,
    onWorkspaceChoiceChange,
}: UserFormPassword) => {
    const { themeStyle } = useThemeContext();
    const styles = dynamicStyles(themeStyle);
    const [fieldType, setFieldType] = useState('password');
    const location = useLocation();
    const isBrandPresentation = workspaceChoice === 'brand';

    const [countryCode, setCountryCode] = useState<CountryData>();
    const [phoneNumberValidity, setPhonenumberValidity] = useState({
        valid: false,
        errorMessage: '',
        isFocused: false,
    });

    useEffect(() => {
        captureReferralCodeFromUrl(`${location.pathname}${location.search}`);
    }, [location.pathname, location.search]);

    const [, setIsFormValid] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        phoneNumber: '',
        fullName: '',
    });

    const [errorState, setErrorState] = useState({
        email: {
            focused: false,
            isErrorState: false,
            errorMessage: '',
        },
        password: {
            focused: false,
            isErrorState: false,
            errorMessage: '',
        },
        phoneNumber: {
            focused: false,
            isErrorState: false,
            errorMessage: '',
        },
        fullName: {
            focused: false,
            isErrorState: false,
            errorMessage: '',
        },
    });

    const [passwordIsValid, setPasswordIsValid] = useState({
        passwordHasLength: false,
        passwordHasCase: false,
        passwordHasNumber: false,
        errorMessage: '',
    });

    const passwordRequirements = [
        { task: '8 characters', validator: passwordIsValid.passwordHasLength },
        { task: '1 uppercase', validator: passwordIsValid.passwordHasCase },
        { task: '1 number', validator: passwordIsValid.passwordHasNumber },
    ];

    const _validatePasswordCharacters = (pass: any) => {
        const passwordLength = validate.validatePasswordLength(pass);
        setPasswordIsValid(prevState => ({
            ...prevState,
            passwordHasLength: passwordLength,
        }));

        const passwordhasNumber = validate.validatePasswordNumber(pass);
        setPasswordIsValid(prevState => ({
            ...prevState,
            passwordHasNumber: passwordhasNumber,
        }));

        const passwordHascase = validate.validatePasswordCase(pass);
        setPasswordIsValid(prevState => ({
            ...prevState,
            passwordHasCase: passwordHascase,
        }));
        validateFormListener();
    };

    const validateFormListener = useCallback(() => {
        const isFormNowValid =
            validate.validateEmail(formData.email) &&
            passwordIsValid.passwordHasCase &&
            passwordIsValid.passwordHasLength &&
            passwordIsValid.passwordHasNumber &&
            validateFullName(formData.fullName);
        setIsFormValid(isFormNowValid);
    }, [
        formData.email,
        formData.fullName,
        passwordIsValid.passwordHasCase,
        passwordIsValid.passwordHasLength,
        passwordIsValid.passwordHasNumber,
    ]);

    const handleTextChange = (text: string, field: 'email' | 'password' | 'phoneNumber' | 'fullName') => {
        setFormData(prevData => ({
            ...prevData,
            [field]: text,
        }));
        if (field === 'password') {
            _validatePasswordCharacters(text);
        }
        validateFormListener();
        if (field === 'fullName') {
            const { firstName, lastName } = splitFullName(text);
            updateFields({ firstName });
            updateFields({ lastName });
        }
        updateFields({ [field]: text });
    };

    const handleOnFocus = (field: Field) => {
        setErrorState(prevState => ({
            ...prevState,
            [field]: {
                focused: false,
                isErrorState: false,
                errorMessage: ``,
            },
        }));
        if (field === 'phoneNumber') {
            setPhonenumberValidity({
                valid: false,
                errorMessage: '',
                isFocused: true,
            });
        }
    };

    const handleOnBlur = (field: Field, errorMsg?: string) => {
        switch (field) {
            case 'fullName':
                if (isEmpty(formData[field]) || !validateFullName(formData[field])) {
                    setErrorState(prevState => ({
                        ...prevState,
                        [field]: {
                            focused: false,
                            isErrorState: true,
                            errorMessage: errorMsg ?? 'Please enter your full name (first and last name)',
                        },
                    }));
                } else {
                    setErrorState(prevState => ({
                        ...prevState,
                        [field]: {
                            focused: false,
                            isErrorState: false,
                            errorMessage: ``,
                        },
                    }));
                }
                break;
            case 'email':
                if (isEmpty(formData[field]) || !validate.validateEmail(formData[field])) {
                    setErrorState(prevState => ({
                        ...prevState,
                        [field]: {
                            focused: false,
                            isErrorState: true,
                            errorMessage: errorMsg ?? `Enter valid ${field}`,
                        },
                    }));
                } else {
                    setErrorState(prevState => ({
                        ...prevState,
                        [field]: {
                            focused: false,
                            isErrorState: false,
                            errorMessage: ``,
                        },
                    }));
                }
                break;
            case 'password':
                const isPasswordValid =
                    passwordIsValid.passwordHasCase &&
                    passwordIsValid.passwordHasLength &&
                    passwordIsValid.passwordHasNumber;

                if (isEmpty(formData[field]) || !isPasswordValid) {
                    setErrorState(prevState => ({
                        ...prevState,
                        [field]: {
                            focused: false,
                            isErrorState: true,
                            errorMessage: errorMsg ?? `Enter a valid ${field}`,
                        },
                    }));
                } else {
                    setErrorState(prevState => ({
                        ...prevState,
                        [field]: {
                            focused: false,
                            isErrorState: false,
                            errorMessage: ``,
                        },
                    }));
                }
                break;
            case 'phoneNumber':
                setPhonenumberValidity({
                    valid: validate.validatePhoneNumber(formData.phoneNumber, countryCode?.countryCode),
                    errorMessage: !validate.validatePhoneNumber(formData.phoneNumber, countryCode?.countryCode)
                        ? `Invalid phone number for ${countryCode?.name}`
                        : '',
                    isFocused: false,
                });

                break;

            default:
                return;
        }
    };

    const handleDevAutofill = useCallback(() => {
        if (!isDevAutofillEnabled()) return;
        const dev = getDevEsSignupFields();

        setFormData({
            fullName: dev.fullName,
            email: dev.email,
            password: dev.password,
            phoneNumber: dev.phoneNumber,
        });
        setPasswordIsValid({
            passwordHasLength: true,
            passwordHasCase: true,
            passwordHasNumber: true,
            errorMessage: '',
        });
        setErrorState({
            email: { focused: false, isErrorState: false, errorMessage: '' },
            password: { focused: false, isErrorState: false, errorMessage: '' },
            phoneNumber: { focused: false, isErrorState: false, errorMessage: '' },
            fullName: { focused: false, isErrorState: false, errorMessage: '' },
        });
        setPhonenumberValidity({
            valid: validate.validatePhoneNumber(dev.phoneNumber, countryCode?.countryCode),
            errorMessage: '',
            isFocused: false,
        });
        updateFields({
            fullName: dev.fullName,
            firstName: dev.firstName,
            lastName: dev.lastName,
            email: dev.email,
            password: dev.password,
            phoneNumber: dev.phoneNumber,
        });
        setIsFormValid(true);
    }, [countryCode?.countryCode, updateFields]);

    return (
        <EsFormWrapper
            title={isBrandPresentation ? 'Welcome to the creators network' : 'Welcome to the creators sandbox.'}
            text={
                isBrandPresentation
                    ? 'Create UGC Campaigns, auditions, collaborate and grow your brand'
                    : ' Explore creative opportunities, collaborate, connect.'
            }
            headerAction={
                <DevAutofillButton
                    onAutofill={handleDevAutofill}
                    title="Dev: fill signup fields"
                    color={theme[themeStyle].highlight}
                />
            }>
            <FormControl style={{ minWidth: '100%', maxHeight: '600px' }}>
                <Box sx={{ width: '100%', mt: 0.5, mb: 2 }}>
                    <Typography
                        sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.14em',
                            color: theme[themeStyle].grey,
                            mb: 1,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                        }}>
                        Sign up as
                    </Typography>
                    <ToggleButtonGroup
                        exclusive
                        fullWidth
                        value={workspaceChoice}
                        onChange={(_, v) => {
                            if (v) onWorkspaceChoiceChange(v);
                        }}
                        sx={{
                            gap: 1,
                            '& .MuiToggleButtonGroup-grouped': {
                                border: 0,
                                borderRadius: '12px !important',
                                mx: 0,
                            },
                        }}>
                        <ToggleButton
                            value="creator"
                            sx={{
                                flex: 1,
                                py: 1.35,
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 15,
                                borderRadius: '12px',
                                color: theme[themeStyle].tag_white,
                                border: `1px solid ${theme[themeStyle].darkGray60}`,
                                fontWeight: 600,
                                gap: 1,
                                bgcolor: alpha(theme[themeStyle].true_white, 0.04),
                                '&.Mui-selected': {
                                    fontWeight: 800,
                                    color: theme[themeStyle].true_white,
                                    borderColor: theme[themeStyle].primary_orange,
                                    bgcolor: alpha(theme[themeStyle].highlight, 0.42),
                                    boxShadow: `inset 0 0 0 1px ${alpha(theme[themeStyle].primary_orange, 0.35)}`,
                                },
                                '&.Mui-selected:hover': {
                                    bgcolor: alpha(theme[themeStyle].highlight, 0.55),
                                },
                                '& .MuiSvgIcon-root': {
                                    fontSize: 22,
                                    color: 'inherit',
                                    opacity: 0.75,
                                },
                                '&.Mui-selected .MuiSvgIcon-root': { opacity: 1 },
                            }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                <AutoAwesomeIcon aria-hidden />
                                Creator
                            </Box>
                        </ToggleButton>
                        <ToggleButton
                            value="brand"
                            sx={{
                                flex: 1,
                                py: 1.35,
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 15,
                                borderRadius: '12px',
                                color: theme[themeStyle].tag_white,
                                border: `1px solid ${theme[themeStyle].darkGray60}`,
                                fontWeight: 600,
                                gap: 1,
                                bgcolor: alpha(theme[themeStyle].true_white, 0.04),
                                '&.Mui-selected': {
                                    fontWeight: 800,
                                    color: theme[themeStyle].true_white,
                                    borderColor: theme[themeStyle].primary_orange,
                                    bgcolor: alpha(theme[themeStyle].highlight, 0.42),
                                    boxShadow: `inset 0 0 0 1px ${alpha(theme[themeStyle].primary_orange, 0.35)}`,
                                },
                                '&.Mui-selected:hover': {
                                    bgcolor: alpha(theme[themeStyle].highlight, 0.55),
                                },
                                '& .MuiSvgIcon-root': {
                                    fontSize: 22,
                                    color: 'inherit',
                                    opacity: 0.75,
                                },
                                '&.Mui-selected .MuiSvgIcon-root': { opacity: 1 },
                            }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                <StorefrontOutlinedIcon aria-hidden />
                                Brand
                            </Box>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                {EXTERNAL_LOGIN && (
                    <>
                        <Button
                            variant="contained"
                            startIcon={<GoogleIcon />}
                            sx={{
                                ...styles.signUpButton,
                                mt: 5,
                            }}>
                            Sign up with Google
                        </Button>

                        <Button
                            variant="contained"
                            startIcon={<AppleIcon />}
                            sx={{
                                ...styles.signUpButton,
                                mt: 2,
                            }}>
                            Sign up with Apple
                        </Button>

                        <Button
                            variant="contained"
                            startIcon={<Facebook />}
                            sx={{
                                ...styles.signUpButton,
                                mt: 2,
                            }}>
                            Sign up with Facebook
                        </Button>

                        <Typography
                            sx={{
                                fontSize: '14px',
                                fontWeight: 400,
                                lineHeight: 2.5,
                                color: theme[themeStyle].form_label,
                                textAlign: 'center',
                                mt: '5px',
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                            }}>
                            Or
                        </Typography>
                    </>
                )}

                <Box sx={{ marginTop: '5px' }}>
                    <CustomTextField
                        label="Full Name"
                        onChange={e => {
                            handleTextChange(e.target.value, 'fullName');
                        }}
                        value={formData.fullName}
                        numberOfLines={1}
                        maxLength={60}
                        importantForAutofill="no"
                        placeholderTextColor={theme[themeStyle].darkGray60}
                        placeholder="Enter your full name"
                        autoComplete="off"
                        onFocus={() => handleOnFocus('fullName')}
                        onBlur={() => handleOnBlur('fullName')}
                        isErrorState={errorState.fullName.isErrorState}
                        errorMsg={errorState.fullName.errorMessage}
                        isFilledState={!isEmpty(formData.fullName)}
                        isFocused={errorState.fullName.focused}
                        textFieldProps={{
                            InputProps: {
                                disableUnderline: true,
                                name: 'fullName',
                                style: {},
                            },
                            required: true,
                            value: fullName,
                        }}
                        inputStyle={{
                            height: 30,
                            paddingBottom: 35,
                        }}
                    />
                </Box>

                <Box sx={{ mt: '5px' }}>
                    <CustomPhoneTextField
                        label="Phone Number"
                        customStyle={{ height: 50, backgroundColor: theme[themeStyle].signUpGrey, borderWidth: 0 }}
                        onChange={(e, data) => {
                            handleTextChange(e, 'phoneNumber');
                            setCountryCode(data);
                            if (onCountryChange) {
                                onCountryChange(data);
                            }
                        }}
                        value={phoneNumber}
                        isErrorState={!phoneNumberValidity.valid}
                        onFocus={() => handleOnFocus('phoneNumber')}
                        onBlur={() => handleOnBlur('phoneNumber')}
                        instruction="An active WhatsApp™ / SMS account is required."
                        errorMsg={phoneNumberValidity.errorMessage}
                        isFocused={phoneNumberValidity.isFocused}
                    />
                </Box>

                <Box sx={{ marginTop: '5px' }}>
                    <CustomTextField
                        label="Email"
                        onChange={e => {
                            handleTextChange(e.target.value, 'email');
                        }}
                        value={formData.email}
                        keyboardType="email-address"
                        numberOfLines={1}
                        maxLength={60}
                        importantForAutofill="no"
                        placeholderTextColor={theme[themeStyle].darkGray60}
                        placeholder={strings.email_address}
                        autoComplete="off"
                        onFocus={() => handleOnFocus('email')}
                        onBlur={() => handleOnBlur('email')}
                        isErrorState={errorState.email.isErrorState}
                        errorMsg={errorState.email.errorMessage}
                        isFilledState={!isEmpty(formData.email)}
                        isFocused={errorState.email.focused}
                        textFieldProps={{
                            InputProps: {
                                disableUnderline: true,
                                name: 'email',
                                style: {
                                    textTransform: 'lowercase',
                                },
                            },
                            required: true,
                            value: email,
                        }}
                        inputStyle={{
                            height: 30,
                            paddingBottom: 35,
                        }}
                    />
                </Box>

                <Box sx={{ marginTop: '5px', marginBottom: '15px' }}>
                    <CustomTextField
                        label="Password"
                        onChange={e => {
                            handleTextChange(e.target.value, 'password');
                        }}
                        value={formData.password}
                        numberOfLines={1}
                        caretHidden
                        secureTextEntry={false}
                        maxLength={60}
                        importantForAutofill="no"
                        placeholderTextColor={theme[themeStyle].darkGray60}
                        placeholder={strings.password}
                        autoComplete="off"
                        onFocus={() => handleOnFocus('password')}
                        onBlur={() => handleOnBlur('password')}
                        isErrorState={errorState.password.isErrorState}
                        errorMsg={errorState.password.errorMessage}
                        isFilledState={!isEmpty(formData.password)}
                        isFocused={errorState.password.focused}
                        fieldTasks
                        fieldTaskList={passwordRequirements}
                        enableCustomButton={!isEmpty(formData.password)}
                        customButton={
                            fieldType === 'password' ? (
                                <VisibilityIcon
                                    onClick={() => setFieldType('text')}
                                    sx={{
                                        color: theme[themeStyle].highlight,
                                    }}
                                />
                            ) : (
                                <VisibilityOffIcon
                                    onClick={() => setFieldType('password')}
                                    sx={{
                                        color: theme[themeStyle].highlight,
                                    }}
                                />
                            )
                        }
                        textFieldProps={{
                            InputProps: {
                                disableUnderline: true,
                                name: 'password',
                                style: {},
                            },
                            type: fieldType,
                            required: true,
                            value: password,
                        }}
                        inputStyle={{ height: 30, paddingBottom: 35 }}
                    />
                </Box>
            </FormControl>
        </EsFormWrapper>
    );
};

const dynamicStyles = (colorScheme: ColorScheme) =>
    StyleSheet.create({
        signUpButton: {
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: 2.5,
            color: theme[colorScheme].form_label,
            textAlign: 'center',
            mt: '5px',
            fontFamily: FONT_ENUM.MEDIUM_TEXT,
            height: 45,
            backgroundColor: theme[colorScheme].signUpGrey,
            boxShadow: 'none',
            borderRadius: 2,
            textTransform: 'initial',
            '&:hover': {
                backgroundColor: 'transparent',
            },
        },
    });

export { Signup };
